import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  AddView,
  CreateLayout, FetchConfigurations,
  FetchViews,
  GetApplicationConfigurations,
  RemoveView,
  ResetLayoutChanges,
  ResizeGridster,
  SaveLayout,
  SetGridsterAPI,
  SetSelectedLayout,
  SetState,
  ToggleSidebar,
  ToggleViewSelection,
  UpdateAutosaveSettings,
  UpdateSecondariesSettings
} from './workspace-management.actions';
import { Configuration, LayoutOption, WorkspaceManagementService, WorkspaceManagementSettings } from 'fi-ui-common';
import { panels } from '../../../pages/issuer/gridster-config';
import { ColumnState } from 'ag-grid-enterprise';
import { Observable, tap } from 'rxjs';
import { GridsterApi } from 'angular-gridster2';

export interface View {
  name: string;
  label: string;
  data?: any;
  component: string;
  selected: boolean;
  x?: number;
  y?: number;
  cols?: number;
  rows?: number;
  columnState?: ColumnState[];
  columnStateVersion?: number;
  precisions?: { colId: number };
}

export const configurationManagement = {
  main: {
    key: "IssuerWorkspaceConfiguration",
    version: 1,
    workspaceKey: 'issuer',
  }
};

export const settingsList: WorkspaceManagementSettings[] = [
  {
    groupLabel: 'Autosave Settings',
    groupId: 'autosaveSettings',
    settings: [
      { label: 'Column Changes', field: 'isColumnAutosaveEnabled', value: true, description: 'Enable or disable automatic saving when columns are added, removed, or rearranged etc. in the workspace.' },
      { label: 'View Changes', field: 'isViewAutosaveEnabled', value: true, description: 'Enable or disable automatic saving when views sized and visibility changed in the workspace.' },
    ]
  }
];

export class WorkspaceManagementModel {
  isSidebarCollapsed: boolean;
  isAutosaveEnabled: boolean;
  layoutOptions: LayoutOption[];
  selectedLayout: Configuration | null;
  views: View[];
  secondariesSettings: {
    bondOrder: Record<string, string[]> | null;
    isCustomSortEnabled: boolean;
  };
  autosaveSettings: {
    isColumnAutosaveEnabled: boolean;
    isViewAutosaveEnabled: boolean;
  };
  gridsterAPI: GridsterApi | null;
}

@State<WorkspaceManagementModel>({
  name: 'workspaceManagement',
  defaults: {
    isSidebarCollapsed: false,
    isAutosaveEnabled: true,
    layoutOptions: [],
    selectedLayout: null,
    views: [...panels],
    secondariesSettings: {
      bondOrder: {},
      isCustomSortEnabled: false
    },
    autosaveSettings: {
      isColumnAutosaveEnabled: true,
      isViewAutosaveEnabled: true
    },
    gridsterAPI: null // TODO: Added a default value for gridsterAPI, but needs to be checked and moved all functionality related with GridsterAPI to the issuer state.
  }
})

@Injectable()
export class WorkspaceManagementState {
  private workspaceService = inject(WorkspaceManagementService);

  @Action(GetApplicationConfigurations)
  getApplicationConfigurations({ patchState, getState }: StateContext<WorkspaceManagementModel>): void {
    const workspaceConfig = this.workspaceService.getWorkspaceConfigurationSnapshot(configurationManagement.main.key);
    const isSidebarCollapsed = workspaceConfig?.configuration?.isSidebarCollapsed ?? getState().isSidebarCollapsed;
    const isAutosaveEnabled = workspaceConfig?.configuration?.isAutosaveEnabled ?? getState().isAutosaveEnabled;
    const secondariesSettings = workspaceConfig?.configuration?.secondariesSettings ?? getState().secondariesSettings;
    const autosaveSettings = {
      isColumnAutosaveEnabled: workspaceConfig?.configuration?.isColumnAutosaveEnabled ?? getState().autosaveSettings.isColumnAutosaveEnabled,
      isViewAutosaveEnabled: workspaceConfig?.configuration?.isViewAutosaveEnabled ?? getState().autosaveSettings.isViewAutosaveEnabled,
    }

    patchState({ isSidebarCollapsed, isAutosaveEnabled, secondariesSettings, autosaveSettings });
  }

  @Action(FetchConfigurations)
  fetchConfigurations({ patchState, getState }: StateContext<WorkspaceManagementModel>): Observable<Configuration[]> {
    return this.workspaceService.fetchConfigurations().pipe(
      tap(configurations => {
        const workspaceConfig = configurations.find(c => c.key?.includes(configurationManagement.main.key) && c.entity === 'workspace');
        const isSidebarCollapsed = workspaceConfig?.configuration?.isSidebarCollapsed ?? getState().isSidebarCollapsed;
        const isAutosaveEnabled = workspaceConfig?.configuration?.isAutosaveEnabled ?? getState().isAutosaveEnabled;
        const secondariesSettings = workspaceConfig?.configuration?.secondariesSettings ?? getState().secondariesSettings;
        const autosaveSettings = {
          isColumnAutosaveEnabled: workspaceConfig?.configuration?.isColumnAutosaveEnabled ?? getState().autosaveSettings.isColumnAutosaveEnabled,
          isViewAutosaveEnabled: workspaceConfig?.configuration?.isViewAutosaveEnabled ?? getState().autosaveSettings.isViewAutosaveEnabled,
        }
        patchState({ isSidebarCollapsed, isAutosaveEnabled, secondariesSettings, autosaveSettings });
      })
    )
  }

  @Action(ToggleSidebar)
  toggleSidebar({ patchState }: StateContext<WorkspaceManagementModel>, { isSidebarCollapsed }: ToggleSidebar): void {
    const workspaceConfig = this.workspaceService.getWorkspaceConfigurationSnapshot(configurationManagement.main.key);
    if (workspaceConfig) {
      workspaceConfig.configuration = { ...workspaceConfig.configuration, isSidebarCollapsed };
      this.workspaceService.saveConfigurations([workspaceConfig], false);
    } else {
      const newWorkspace = this.workspaceService.createNewConfigurationItem(configurationManagement.main.key, configurationManagement.main.workspaceKey, configurationManagement.main.version, { isSidebarCollapsed }, 'workspace');
      this.workspaceService.saveConfigurations([newWorkspace], false);
    }

    patchState({ isSidebarCollapsed })
  }

  @Action(CreateLayout)
  createLayout({ patchState, getState }: StateContext<WorkspaceManagementModel>, { label, views }: CreateLayout): void {
    const viewsConfig = views || getState().views;
    const newLayout = this.workspaceService.createNewConfigurationItem(
      configurationManagement.main.key,
      label,
      configurationManagement.main.version,
      { views: viewsConfig.filter(view => view.component !== 'TranchesCompleted') },
      'layout'
    );
    this.workspaceService.saveConfigurations([newLayout], false);
    patchState({ views: viewsConfig });
  }

  @Action(SetSelectedLayout)
  setSelectedLayout({ patchState }: StateContext<WorkspaceManagementModel>, { selectedLayout }: SetSelectedLayout): void {
    patchState({ selectedLayout });
  }

  @Action(UpdateSecondariesSettings)
  updateSecondariesSettings({ patchState, getState }: StateContext<WorkspaceManagementModel>, { settings }: UpdateSecondariesSettings): void {
    const currentSettings = getState().secondariesSettings;
    const updatedSettings = {
      ...currentSettings,
      ...settings,
      bondOrder: {
        ...currentSettings.bondOrder,
        ...(settings.bondOrder || {})
      }
    };

    const workspaceConfig = this.workspaceService.getWorkspaceConfigurationSnapshot(configurationManagement.main.key);
    if (workspaceConfig) {
      workspaceConfig.configuration = {
        ...workspaceConfig.configuration,
        secondariesSettings: updatedSettings
      };
      this.workspaceService.saveConfigurations([workspaceConfig], false);
    } else {
      const newWorkspace = this.workspaceService.createNewConfigurationItem(
        configurationManagement.main.key,
        configurationManagement.main.workspaceKey,
        configurationManagement.main.version,
        { secondariesSettings: updatedSettings }, 'workspace'
      );
      this.workspaceService.saveConfigurations([newWorkspace], false);
    }

    patchState({ secondariesSettings: updatedSettings });
  }

  @Action(FetchViews)
  fetchViews({ patchState, getState }: StateContext<WorkspaceManagementModel>): void {
    const selectedLayout = getState().selectedLayout;
    if (selectedLayout === null) {
      patchState({ views: JSON.parse(JSON.stringify(panels)) });
    } else {
      const views = selectedLayout?.configuration?.views ? JSON.parse(JSON.stringify(selectedLayout.configuration.views)) : JSON.parse(JSON.stringify(panels));
      patchState({ views });
    }
  }

  @Action(ToggleViewSelection)
  toggleViewSelection({ patchState, getState }: StateContext<WorkspaceManagementModel>, { payload }: ToggleViewSelection): void {
    const views = getState().views.map(view => {
      if (view.label === payload.label) {
        view.selected = !payload.selected
      }
      return view;
    })
    patchState({ views })
  }

  @Action(RemoveView)
  removeView({ patchState, getState }: StateContext<WorkspaceManagementModel>, { label }: RemoveView): void {
    const views = getState().views.filter(view => view.label !== label)
    patchState({ views })
  }

  @Action(AddView)
  addView({ patchState, getState }: StateContext<WorkspaceManagementModel>, { view }: AddView): void {
    patchState({ views: [...getState().views, view] })
  }

  @Action(SetState)
  setState({ patchState, getState }: StateContext<WorkspaceManagementModel>, { payload }: SetState): void {
    const views = getState().views.map(view => view.label === payload.label ? ({ ...view, ...payload }) : view);
    patchState({ views })
  }

  @Action(SaveLayout)
  saveLayout({ getState, patchState }: StateContext<WorkspaceManagementModel>, { displayToast }: SaveLayout): void {
    const layout = getState().selectedLayout;
    if (layout) {
      const views = getState().views.filter(view => view.component !== 'TranchesCompleted');
      layout.configuration = { ...layout.configuration, views };
      this.workspaceService.saveConfigurations([layout], !!displayToast);
      patchState({ views: structuredClone(views) })
    }
  }

  @Action(ResetLayoutChanges)
  resetLayoutChanges({ getState, patchState }: StateContext<WorkspaceManagementModel>): void {
    const selectedLayout = getState().selectedLayout;
    const views = selectedLayout?.configuration?.views ? JSON.parse(JSON.stringify(selectedLayout.configuration?.views)) : JSON.parse(JSON.stringify(panels));
    patchState({ views });
  }

  @Action(UpdateAutosaveSettings)
  updateAutosaveSettings({ getState, patchState }: StateContext<WorkspaceManagementModel>, { settings }: UpdateAutosaveSettings): void {
    const currentSettings = getState().autosaveSettings;
    const updatedSettings = { ...currentSettings, ...settings };
    const workspaceConfig = this.workspaceService.getWorkspaceConfigurationSnapshot(configurationManagement.main.key);
    const isAutosaveEnabled = !(!settings.isColumnAutosaveEnabled && !settings.isViewAutosaveEnabled);
    if (workspaceConfig) {
      workspaceConfig.configuration = { ...workspaceConfig.configuration, ...settings, isAutosaveEnabled };
      this.workspaceService.saveConfigurations([workspaceConfig], false);
    } else {
      const newWorkspace = this.workspaceService.createNewConfigurationItem(configurationManagement.main.key, configurationManagement.main.workspaceKey, configurationManagement.main.version, { ...settings, isAutosaveEnabled }, 'workspace');
      this.workspaceService.saveConfigurations([newWorkspace], false);
    }
    patchState({ autosaveSettings: updatedSettings, isAutosaveEnabled });
  }

  private getViewToSave(view: View, autosaveSettings: { isColumnAutosaveEnabled: boolean; isViewAutosaveEnabled: boolean; }, layout: Configuration): View {
    const viewToSave = autosaveSettings.isViewAutosaveEnabled ? { ...view } : layout.configuration.views?.find(v => v.label === view.label);

    if (autosaveSettings.isColumnAutosaveEnabled) {
      viewToSave.columnState = view?.columnState;
    } else {
      const originalColumnState = layout.configuration.views?.find(v => v.label === view.label)?.columnState;
      if (originalColumnState) {
        viewToSave.columnState = originalColumnState;
      } else {
        delete viewToSave['columnState'];
      }
    }

    return { ...viewToSave };
  }

  @Action(SetGridsterAPI)
  setGridsterAPI({ patchState }: StateContext<WorkspaceManagementModel>, { gridsterAPI }: SetGridsterAPI): void {
    patchState({ gridsterAPI });
  }

  @Action(ResizeGridster)
  resizeGridster({ getState }: StateContext<WorkspaceManagementModel>): void {
    const gridsterAPI = getState().gridsterAPI;
    if (gridsterAPI) {
      gridsterAPI.resize();
    }
  }
}
